import * as React from "react";
import { Currency, ConsiderationValue, TradingInstructionDirection, TradeSubType } from "swan-public-api-client";
import { FormattedNumber, FormattedDate, IntlShape, FormattedTime } from "react-intl";
import { FormattedCurrency } from "../Localisation/FormattedCurrency";
import { FormattedPrice } from "../Localisation/FormattedPrice";
import { tradeSubTypeToStringConverter, tradingInstructionDirectionToStringConverter, quantityDirectionToStringConverter, currencyToStringConverter } from "../Localisation/Converters";

function undefinedRenderer<TRecord, TProperty>(
  valueSelector: (record: TRecord) => TProperty | null | undefined,
  renderer: (value: TProperty) => JSX.Element
) {
  return (_: any, record: TRecord, __: number) => {
    const value = valueSelector(record);
    if (value === undefined
      || value === null) {
      return ("–");
    }

    return renderer(value);
  }
};

export function currencyNameRenderer<T>(
  valueSelector: (x: T) => number | null | undefined,
  currencies: Map<number, Currency>,
  intl: IntlShape
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    const currency = value !== undefined && value !== null
      ? currencies.get(value)
      : undefined;
    return currencyToStringConverter(currency, intl);
  };
}

export function currencyRenderer<T>(
  valueSelector: (x: T) => number | null | undefined,
  currency: Currency
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    return (<FormattedCurrency value={value} currency={currency} />);
  }
}

export function currencyViaRecordRenderer<T>(
  valueSelector: (x: T) => number | null | undefined,
  currencyIdSelector: (x: T) => number | null | undefined,
  currencies: Map<number, Currency>
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    const currencyId = currencyIdSelector(record);
    const currency = currencyId !== undefined && currencyId !== null
      ? currencies.get(currencyId)
      : undefined;

    return (<FormattedCurrency value={value} currency={currency} />);
  };
}

export function currencyRoundedValueRenderer<T>(
  valueSelector: (x: T) => number,
  currencyIdSelector: (x: T) => number,
  currencies: Map<number, Currency>
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    const currencyId = currencyIdSelector(record);
    const currency = currencies.get(currencyId);
    if (currency) {
      return (<FormattedNumber value={value} minimumFractionDigits={currency.Exponent} />)
    } else {
      return (<FormattedNumber value={value} />)
    }
  };
}

export function considerationRenderer<T>(
  valueSelector: (x: T) => ConsiderationValue | null | undefined,
  currencies: Map<number, Currency>
) {
  const subValueExtractor = <TReturn extends unknown>(
    considerationValue: ConsiderationValue | null | undefined,
    valueExtractor: (considerationValue: ConsiderationValue) => TReturn
  ) => {
    if (considerationValue === undefined) {
      return undefined;
    }
    if (considerationValue === null) {
      return null;
    }

    return valueExtractor(considerationValue);
  };

  const currencyRendererValueSelector = (x: T) => {
    const value = valueSelector(x);
    return subValueExtractor(
      value,
      considerationValue => considerationValue.Value
    );
  };
  const currencyRendererCurrencyIdSelector = (x: T) => {
    const value = valueSelector(x);
    return subValueExtractor(
      value,
      considerationValue => considerationValue.CurrencyId
    );
  };

  return currencyViaRecordRenderer(
    currencyRendererValueSelector,
    currencyRendererCurrencyIdSelector,
    currencies
  );
}

export function dateRenderer<T>(
  valueSelector: (x: T) => Date | null | undefined
) {
  return undefinedRenderer(
    valueSelector,
    value => <FormattedDate value={value} />
  );
}

export function dateTimeRenderer<T>(
  valueSelector: (x: T) => Date | null | undefined
) {
  return undefinedRenderer(
    valueSelector,
    value => <span><FormattedDate value={value} /> <FormattedTime value={value} /></span>
  );
}

export function priceRenderer<T>(
  valueSelector: (x: T) => number | null | undefined,
  currency: Currency
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    return (<FormattedPrice value={value} currency={currency} />);
  };
}

export function priceWithCurrencyViaRecordRenderer<T>(
  valueSelector: (x: T) => number | null | undefined,
  currencyIdSelector: (x: T) => number | null | undefined,
  currencies: Map<number, Currency>
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    const currencyId = currencyIdSelector(record);
    const currency = currencyId !== undefined && currencyId !== null
      ? currencies.get(currencyId)
      : undefined;

    return (<FormattedPrice value={value} currency={currency} />);
  }
}

export function directionRenderer<T>(
  valueSelector: (x: T) => TradingInstructionDirection,
  intl: IntlShape
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    return tradingInstructionDirectionToStringConverter(value, intl);
  };
}

export function quantityDirectionRenderer<T>(
  valueSelector: (x: T) => number,
  intl: IntlShape
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    return quantityDirectionToStringConverter(value, intl);
  };
}

export function tradeSubTypeRenderer<T>(
  valueSelector: (x: T) => TradeSubType,
  intl: IntlShape
) {
  return (_: any, record: T, __: number) => {
    const value = valueSelector(record);
    return tradeSubTypeToStringConverter(value, intl);
  };
}
